import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useTheme } from '@mui/material/styles';
import { 
  TextField, 
  Autocomplete, 
  Checkbox, 
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  TablePagination,
  Paper,
  Typography,
  Box,
  IconButton,
  Chip
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import moment from 'moment';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import ClearIcon from '@mui/icons-material/Clear';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import LastPageIcon from '@mui/icons-material/LastPage';
import DetailsIcon from '@mui/icons-material/Details';

// Styles
import { TableContStyle, TableHeadStyle } from '../../../Styles/TableStyles';
import { HeadingWrap, Span } from '../../../Styles/CommonStyles';

// Utilities
import ExcelDownload from '../../../data/exceldownload';

// API Queries  
import { useGetConsumTransactionsFilteredQuery } from '../../../features/consumtransactions/consumTransApiSlice';
import { useGetUsersQuery } from '../../../features/users/usersApiSlice';
import { useGetAssetsFinalQuery } from '../../../features/addassets/addassetfinalApiSlice';

// Components
import FilteredConsumTransFetcher from './FilteredConsumTransFetcher';
import DropdownConsumOptionsFetcher from './DropdownConsumOptionsFetcher';

// Utilities
import nameStringFn from '../../../data/nameStringFn';

const checkedIcon = <CheckBoxIcon fontSize="small" />;
const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;

const Button2Div = styled.div`
  display: flex;
  justify-content: end;
`;

const FilterContainer = styled(Box)`
  background-color: #f5f5f5;
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 20px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
`;

const FilterGrid = styled(Box)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 16px;
`;

// Common ellipsis style for table cells
const ellipsisStyle = {
  fontSize: { xs: 11, sm: 12, md: 13, lg: 14 },
  padding: { xs: '8px 4px', sm: '16px 8px' },
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

// Debounce function
function debounce(func, delay) {
  let timer;
  return function (...args) {
    clearTimeout(timer);
    timer = setTimeout(() => func.apply(this, args), delay);
  };
}

// Table Pagination Actions Component
function TablePaginationActions(props) {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === 'rtl' ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === 'rtl' ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </Box>
  );
}

const ConsumTransac = ({ filterProps = {} }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [downloadData, setDownloadData] = useState({});
  const [allData, setAllData] = useState([]);
  const [dropdownData, setDropdownData] = useState([]); // For filter dropdown options
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailModalData, setDetailModalData] = useState({});
  
  // Filter states with permanent filters support
  const [filters, setFilters] = useState({
    transac_id: '',
    unique_req_id: '', // Human-readable request ID search
    transac_type: [],
    consumable: [],
    partnumber: '', // Text search for part number
    from_office: [],
    to_office: [],
    from_desig: [],
    to_desig: [],
    from_store: [],
    to_store: [],
    from_userid: '', // Text search for user email
    to_userid: '', // Text search for user email
    asset_id: '', // Backend filter (OEM tags - text search)
    from_quantity: '',
    to_quantity: '',
    from_date: null,
    to_date: null,
    // Permanent filters (prefixed with perm_)
    perm_transac_type: [],
    perm_from_office: [],
    perm_to_office: [],
    perm_from_desig: [],
    perm_to_desig: [],
    perm_from_store: [],
    perm_to_store: [],
    perm_from_userid: [],
    perm_to_userid: []
  });

  // State for table data
  const [tabledata, setTabledata] = useState([]);

  // API call for filtered transactions with pagination
  const { data: finalData, isLoading: transactionsLoading, error: transactionsError } = useGetConsumTransactionsFilteredQuery({
    page: page + 1, // Convert MUI's 0-based index to 1-based API pagination
    limit: rowsPerPage,
    filters,
  });

  // API call for all users
  const {
    data: users,
    isLoading: usersLoading
  } = useGetUsersQuery('userslist', {
    refetchOnFocus: true,
    refetchOnMountOrArgChange: true
  });

  // API call for assets (for linked asset info)
  const {
    data: allassets,
    isLoading: assetsLoading
  } = useGetAssetsFinalQuery('allassetlist', {
    refetchOnFocus: true,
    refetchOnMountOrArgChange: true
  });

  const assets = useMemo(() => {
    const allassetdata = allassets ? Object.values(allassets?.entities) : [];
    return allassetdata || [];
  }, [allassets]);

  // Extract only permanent filters for dropdown options
  const permanentFiltersOnly = useMemo(() => {
    return {
      perm_transac_type: filters.perm_transac_type,
      perm_from_office: filters.perm_from_office,
      perm_to_office: filters.perm_to_office,
      perm_from_desig: filters.perm_from_desig,
      perm_to_desig: filters.perm_to_desig,
      perm_from_store: filters.perm_from_store,
      perm_to_store: filters.perm_to_store,
      perm_from_userid: filters.perm_from_userid,
      perm_to_userid: filters.perm_to_userid
    };
  }, [
    filters.perm_transac_type,
    filters.perm_from_office,
    filters.perm_to_office,
    filters.perm_from_desig,
    filters.perm_to_desig,
    filters.perm_from_store,
    filters.perm_to_store,
    filters.perm_from_userid,
    filters.perm_to_userid
  ]);

  // Apply filterProps if provided (permanent filters from parent component)
  useEffect(() => {
    if (filterProps) {
      const newFilters = {};
      for (let key in filterProps) {
        if (filterProps[key] !== undefined) {
          newFilters[key] = filterProps[key];
        }
      }
      if (Object.keys(newFilters).length > 0) {
        setFilters((prevFilters) => ({
          ...prevFilters,
          ...newFilters
        }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterProps]);

  useEffect(() => {
    if (finalData) {
      setTabledata(finalData?.transactions);
    }
  }, [finalData]);

  // Extract unique values for filter options from dropdownData (unfiltered data for dropdowns)
  const filterOptions = useMemo(() => {
    const allTransactions = dropdownData?.transactions || [];
    
    if (!allTransactions.length) return {};

    const fromOffices = [...new Set(allTransactions.map(t => t.from_office))].filter(Boolean);
    const toOffices = [...new Set(allTransactions.map(t => t.to_office))].filter(Boolean);
    const fromDesignations = [...new Set(allTransactions.map(t => t.from_desig))].filter(Boolean);
    const toDesignations = [...new Set(allTransactions.map(t => t.to_desig))].filter(Boolean);
    const fromStores = [...new Set(allTransactions.map(t => t.from_store))].filter(Boolean);
    const toStores = [...new Set(allTransactions.map(t => t.to_store))].filter(Boolean);
    const fromUsers = [...new Set(allTransactions.map(t => t.from_userid))].filter(Boolean);
    const toUsers = [...new Set(allTransactions.map(t => t.to_userid))].filter(Boolean);
    const assetTags = [...new Set(allTransactions.map(t => t.asset_id))].filter(Boolean);

    return {
      transactionTypes: [...new Set(allTransactions.map(t => t.transac_type))].filter(Boolean),
      consumables: [...new Set(allTransactions.map(t => t.consumable))].filter(Boolean),
      partnumbers: [...new Set(allTransactions.map(t => t.partnumber))].filter(Boolean),
      assetTags,
      fromOffices,
      toOffices,
      fromDesignations,
      toDesignations,
      fromStores,
      toStores,
      fromUsers,
      toUsers
    };
  }, [dropdownData]);

  // Debug logging
  useEffect(() => {
    console.log('Current filters:', filters);
    console.log('API Response - finalData:', finalData);
    console.log('API Error:', transactionsError);
    console.log('Table data:', tabledata);
    console.log('Dropdown data:', dropdownData);
    console.log('Filter options:', filterOptions);
  }, [filters, finalData, transactionsError, tabledata, dropdownData, filterOptions]);

  // Pagination and other handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page when rows per page changes
  };

  // Immediate handler for text inputs (no debounce)
  const handleTextInputChange = (event) => {
    const name = event.target.name;
    const value = event.target.value;
    
    setFilters((prevFilters) => ({
      ...prevFilters,
      [name]: value,
    }));
    
    // Reset to first page when filters change
    setPage(0);
  };

  // Debounced handler for autocomplete and date fields
  const handleSearchChange = debounce((event, newValue, name) => {
    let value;

    // If a date is selected, check if it's valid and convert to ISO format
    if (newValue?.$d) {
      try {
        const dateObj = newValue.toDate();
        // Check if date is valid
        if (dateObj && !isNaN(dateObj.getTime())) {
          value = dateObj.toISOString();
        } else {
          console.warn('Invalid date value:', newValue);
          value = null;
        }
      } catch (error) {
        console.error('Error converting date:', error);
        value = null;
      }
    } else {
      value = newValue;
    }

    setFilters((prevFilters) => ({
      ...prevFilters,
      [name]: value,
    }));
    
    // Reset to first page when filters change
    setPage(0);
  }, 500);

  const handleDetailsClick = (e, transaction) => {
    setDetailModalData(transaction);
    setDetailModalOpen(true);
  };

  const handleDetailModalClose = () => {
    setDetailModalOpen(false);
  };

  const clearAllFilters = () => {
    setFilters({
      transac_id: '',
      unique_req_id: '',
      transac_type: [],
      consumable: [],
      partnumber: '',
      from_office: [],
      to_office: [],
      from_desig: [],
      to_desig: [],
      from_store: [],
      to_store: [],
      from_userid: '',
      to_userid: '',
      asset_id: '',
      from_quantity: '',
      to_quantity: '',
      from_date: null,
      to_date: null,
      // Keep permanent filters intact
      perm_transac_type: filters.perm_transac_type,
      perm_from_office: filters.perm_from_office,
      perm_to_office: filters.perm_to_office,
      perm_from_desig: filters.perm_from_desig,
      perm_to_desig: filters.perm_to_desig,
      perm_from_store: filters.perm_from_store,
      perm_to_store: filters.perm_to_store,
      perm_from_userid: filters.perm_from_userid,
      perm_to_userid: filters.perm_to_userid
    });
    setPage(0);
  };

  // Prepare download data from allData (fetched with limit 99999)
  useEffect(() => {
    if (allData?.transactions && allData.transactions.length > 0) {
      const finalDownloadData = allData.transactions.map(transaction => {
        // Get linked asset info if available
        // asset_id now contains oem_tag, not MongoDB ObjectId
        let assetInfo = 'N/A';
        if (transaction.asset_id) {
          const asset = assets.find(a => a.oem_tag === transaction.asset_id);
          assetInfo = asset ? `${asset.name} (${transaction.asset_id})` : transaction.asset_id;
        }
        
        return {
          'Transaction ID': transaction.transac_id || '',
          'Request ID': transaction.unique_req_id || 'N/A',
          'Transaction Type': transaction.transac_type || '',
          'Consumable': transaction.consumable || '',
          'Part Number': transaction.partnumber || '',
          'Quantity': transaction.quantity || 0,
          'Linked Asset': assetInfo,
          'From Office': transaction.from_office || '',
          'To Office': transaction.to_office || '',
          'From Designation': transaction.from_desig || '',
          'To Designation': transaction.to_desig || '',
          'From Store': transaction.from_store || '',
          'To Store': transaction.to_store || '',
          'From User': transaction.from_userid || '',
          'To User': transaction.to_userid || '',
          'Date': moment(transaction.time_stamp).format('DD/MM/YYYY HH:mm')
        };
      });
      setDownloadData(finalDownloadData);
    }
  }, [allData, assets]);

  if (assetsLoading || transactionsLoading || usersLoading) {
    return (
      <div style={{ padding: '20px' }}>
        <Typography variant="h4" gutterBottom>
          Loading Consumable Transactions...
        </Typography>
      </div>
    );
  }

  if (transactionsError) {
    return (
      <div style={{ padding: '20px' }}>
        <Typography variant="h4" gutterBottom color="error">
          Error Loading Transactions
        </Typography>
        <Typography variant="body1">
          {transactionsError?.data?.message || transactionsError?.error || 'Unknown error occurred'}
        </Typography>
        <Typography variant="caption" sx={{ mt: 2, display: 'block' }}>
          Check browser console for more details
        </Typography>
      </div>
    );
  }

  return (
    <>
      {/* Fetch filtered data for Excel download */}
      <FilteredConsumTransFetcher filters={filters} onDataFetched={setAllData} />
      
      {/* Fetch unfiltered data (with only permanent filters) for dropdown options */}
      <DropdownConsumOptionsFetcher permanentFilters={permanentFiltersOnly} onDataFetched={setDropdownData} />

      {/* Heading */}
      <HeadingWrap>
        <Span>Consumable Transactions</Span>
      </HeadingWrap>

      {/* Excel Download Button */}
      {(allData?.transactions && downloadData?.length > 0) && (
        <Button2Div>
          <ExcelDownload data={downloadData} filename={"consumableTransactionDetail"} sheetName={"sheet1"} />
        </Button2Div>
      )}

      {/* Filter Section - Above Table */}
      <FilterContainer>
        <Typography variant="h6" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
          Search Filters
        </Typography>
        <FilterGrid>
          {/* Transaction Type */}
          <Autocomplete
            multiple
            id="transac-type-filter"
            onChange={(event, newValue) => handleSearchChange(event, newValue, "transac_type")}
            size="small"
            value={filters?.transac_type}
            options={filterOptions.transactionTypes || []}
            disableCloseOnSelect
            getOptionLabel={(option) => option}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox
                  icon={icon}
                  checkedIcon={checkedIcon}
                  style={{ marginRight: 8 }}
                  checked={selected}
                  size="small"
                />
                {option}
              </li>
            )}
            renderInput={(params) => (
              <TextField {...params} label="Transaction Type" sx={{ background: "white" }} />
            )}
          />

          {/* Transaction ID */}
          <TextField
            sx={{ background: "white" }}
            name="transac_id"
            onChange={handleTextInputChange}
            label="Transaction ID"
            size="small"
            variant="outlined"
            fullWidth
            value={filters.transac_id}
          />

          {/* Request ID */}
          <TextField
            sx={{ background: "white" }}
            name="unique_req_id"
            onChange={handleTextInputChange}
            label="Request ID"
            size="small"
            variant="outlined"
            fullWidth
            value={filters.unique_req_id}
            placeholder="Search by request ID..."
          />

          {/* Consumable */}
          <Autocomplete
            multiple
            id="consumable-filter"
            onChange={(event, newValue) => handleSearchChange(event, newValue, "consumable")}
            size="small"
            value={filters?.consumable}
            options={filterOptions.consumables || []}
            disableCloseOnSelect
            getOptionLabel={(option) => option}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox
                  icon={icon}
                  checkedIcon={checkedIcon}
                  style={{ marginRight: 8 }}
                  checked={selected}
                  size="small"
                />
                {option}
              </li>
            )}
            renderInput={(params) => (
              <TextField {...params} label="Consumable" sx={{ background: "white" }} />
            )}
          />

          {/* Part Number */}
          <TextField
            sx={{ background: "white" }}
            name="partnumber"
            onChange={handleTextInputChange}
            label="Part Number"
            size="small"
            variant="outlined"
            fullWidth
            value={filters.partnumber}
            placeholder="Search by part number..."
          />

          {/* Asset Tag */}
          <TextField
            sx={{ background: "white" }}
            name="asset_id"
            onChange={handleTextInputChange}
            label="Asset Tag"
            size="small"
            variant="outlined"
            fullWidth
            value={filters.asset_id}
            placeholder="Search by OEM tag..."
          />

          {/* From Quantity */}
          <TextField
            sx={{ background: "white" }}
            name="from_quantity"
            onChange={handleTextInputChange}
            label="Min Quantity"
            size="small"
            type="number"
            variant="outlined"
            fullWidth
            value={filters.from_quantity}
          />

          {/* To Quantity */}
          <TextField
            sx={{ background: "white" }}
            name="to_quantity"
            onChange={handleTextInputChange}
            label="Max Quantity"
            size="small"
            type="number"
            variant="outlined"
            fullWidth
            value={filters.to_quantity}
          />

          {/* From Office */}
          <Autocomplete
            multiple
            id="from-office-filter"
            onChange={(event, newValue) => handleSearchChange(event, newValue, "from_office")}
            size="small"
            value={filters?.from_office}
            options={filterOptions.fromOffices || []}
            disableCloseOnSelect
            getOptionLabel={(option) => option}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox
                  icon={icon}
                  checkedIcon={checkedIcon}
                  style={{ marginRight: 8 }}
                  checked={selected}
                  size="small"
                />
                {option}
              </li>
            )}
            renderInput={(params) => (
              <TextField {...params} label="From Office" sx={{ background: "white" }} />
            )}
          />

          {/* To Office */}
          <Autocomplete
            multiple
            id="to-office-filter"
            onChange={(event, newValue) => handleSearchChange(event, newValue, "to_office")}
            size="small"
            value={filters?.to_office}
            options={filterOptions.toOffices || []}
            disableCloseOnSelect
            getOptionLabel={(option) => option}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox
                  icon={icon}
                  checkedIcon={checkedIcon}
                  style={{ marginRight: 8 }}
                  checked={selected}
                  size="small"
                />
                {option}
              </li>
            )}
            renderInput={(params) => (
              <TextField {...params} label="To Office" sx={{ background: "white" }} />
            )}
          />

          {/* From Designation */}
          <Autocomplete
            multiple
            id="from-designation-filter"
            onChange={(event, newValue) => handleSearchChange(event, newValue, "from_desig")}
            size="small"
            value={filters?.from_desig}
            options={filterOptions.fromDesignations || []}
            disableCloseOnSelect
            getOptionLabel={(option) => option}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox
                  icon={icon}
                  checkedIcon={checkedIcon}
                  style={{ marginRight: 8 }}
                  checked={selected}
                  size="small"
                />
                {option}
              </li>
            )}
            renderInput={(params) => (
              <TextField {...params} label="From Designation" sx={{ background: "white" }} />
            )}
          />

          {/* To Designation */}
          <Autocomplete
            multiple
            id="to-designation-filter"
            onChange={(event, newValue) => handleSearchChange(event, newValue, "to_desig")}
            size="small"
            value={filters?.to_desig}
            options={filterOptions.toDesignations || []}
            disableCloseOnSelect
            getOptionLabel={(option) => option}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox
                  icon={icon}
                  checkedIcon={checkedIcon}
                  style={{ marginRight: 8 }}
                  checked={selected}
                  size="small"
                />
                {option}
              </li>
            )}
            renderInput={(params) => (
              <TextField {...params} label="To Designation" sx={{ background: "white" }} />
            )}
          />

          {/* From Store */}
          <Autocomplete
            multiple
            id="from-store-filter"
            onChange={(event, newValue) => handleSearchChange(event, newValue, "from_store")}
            size="small"
            value={filters?.from_store}
            options={filterOptions.fromStores || []}
            disableCloseOnSelect
            getOptionLabel={(option) => option}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox
                  icon={icon}
                  checkedIcon={checkedIcon}
                  style={{ marginRight: 8 }}
                  checked={selected}
                  size="small"
                />
                {option}
              </li>
            )}
            renderInput={(params) => (
              <TextField {...params} label="From Store" sx={{ background: "white" }} />
            )}
          />

          {/* To Store */}
          <Autocomplete
            multiple
            id="to-store-filter"
            onChange={(event, newValue) => handleSearchChange(event, newValue, "to_store")}
            size="small"
            value={filters?.to_store}
            options={filterOptions.toStores || []}
            disableCloseOnSelect
            getOptionLabel={(option) => option}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox
                  icon={icon}
                  checkedIcon={checkedIcon}
                  style={{ marginRight: 8 }}
                  checked={selected}
                  size="small"
                />
                {option}
              </li>
            )}
            renderInput={(params) => (
              <TextField {...params} label="To Store" sx={{ background: "white" }} />
            )}
          />

          {/* From User */}
          <TextField
            sx={{ background: "white" }}
            name="from_userid"
            onChange={handleTextInputChange}
            label="From User"
            size="small"
            variant="outlined"
            fullWidth
            value={filters.from_userid}
            placeholder="Search by email..."
          />

          {/* To User */}
          <TextField
            sx={{ background: "white" }}
            name="to_userid"
            onChange={handleTextInputChange}
            label="To User"
            size="small"
            variant="outlined"
            fullWidth
            value={filters.to_userid}
            placeholder="Search by email..."
          />

          {/* From Date */}
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ position: 'relative' }}>
              <DatePicker
                label="From Date"
                maxDate={filters?.to_date ? dayjs(filters.to_date) : null}
                slotProps={{ 
                  textField: { 
                    size: "small", 
                    fullWidth: true,
                    sx: { backgroundColor: "white" }
                  },
                  field: { clearable: true, onClear: () => {
                    setFilters(prev => ({ ...prev, from_date: null }));
                    setPage(0);
                  }}
                }}
                value={filters?.from_date ? dayjs(filters.from_date) : null}
                format="DD/MM/YYYY"
                onChange={(newValue) => {
                  // Validate date before passing to handler
                  if (newValue === null || (newValue && dayjs(newValue).isValid())) {
                    handleSearchChange(null, newValue, "from_date");
                  } else {
                    console.warn('Invalid from_date selected, ignoring');
                  }
                }}
                onError={(error) => {
                  if (error) {
                    console.warn('Date picker error:', error);
                  }
                }}
              />
            </Box>
          </LocalizationProvider>

          {/* To Date */}
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ position: 'relative' }}>
              <DatePicker
                label="To Date"
                minDate={filters?.from_date ? dayjs(filters.from_date) : null}
                slotProps={{ 
                  textField: { 
                    size: "small", 
                    fullWidth: true,
                    sx: { backgroundColor: "white" }
                  },
                  field: { clearable: true, onClear: () => {
                    setFilters(prev => ({ ...prev, to_date: null }));
                    setPage(0);
                  }}
                }}
                value={filters?.to_date ? dayjs(filters.to_date) : null}
                format="DD/MM/YYYY"
                onChange={(newValue) => {
                  // Validate date before passing to handler
                  if (newValue === null || (newValue && dayjs(newValue).isValid())) {
                    handleSearchChange(null, newValue, "to_date");
                  } else {
                    console.warn('Invalid to_date selected, ignoring');
                  }
                }}
                onError={(error) => {
                  if (error) {
                    console.warn('Date picker error:', error);
                  }
                }}
              />
            </Box>
          </LocalizationProvider>
        </FilterGrid>
        
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Button 
            variant="outlined" 
            color="secondary" 
            onClick={clearAllFilters}
            startIcon={<ClearIcon />}
          >
            Clear All Filters
          </Button>
        </Box>
      </FilterContainer>

      <div style={{ 
        width: '100%', 
        padding: '8px',
        boxSizing: 'border-box'
      }}>
        <TableContainer 
          component={Paper} 
          sx={{ 
            backgroundColor: TableContStyle.backgroundColor,
            width: '100%',
            margin: { xs: '8px 0', sm: '16px 0' },
            boxShadow: { xs: 1, sm: 3 }
          }}
        >
          <Table 
            sx={{ 
              width: '100%',
              tableLayout: 'auto'
            }} 
            aria-label="consumable transactions table"
          >
            <TableHead style={{ 
              backgroundColor: TableHeadStyle.backgroundColor,
              position: 'sticky',
              top: 0,
              zIndex: 1
            }}>
              <TableRow>
                <TableCell style={TableHeadStyle} sx={{ width: "3%" }}>
                  Sl.No.
                </TableCell>
                <TableCell style={TableHeadStyle} sx={{ width: "8%" }} align="left">
                  Transaction ID
                </TableCell>
                <TableCell style={TableHeadStyle} sx={{ width: "8%" }} align="left">
                  Request ID
                </TableCell>
                <TableCell style={TableHeadStyle} sx={{ width: "7%" }} align="left">
                  Type
                </TableCell>
                <TableCell style={TableHeadStyle} sx={{ width: "9%" }} align="left">
                  Consumable
                </TableCell>
                <TableCell style={TableHeadStyle} sx={{ width: "8%" }} align="left">
                  Part Number
                </TableCell>
                <TableCell style={TableHeadStyle} sx={{ width: "7%" }} align="left">
                  Asset Tag
                </TableCell>
                <TableCell style={TableHeadStyle} sx={{ width: "5%" }} align="center">
                  Qty
                </TableCell>
                <TableCell style={TableHeadStyle} sx={{ width: "9%" }} align="left">
                  Office
                </TableCell>
                <TableCell style={TableHeadStyle} sx={{ width: "9%" }} align="left">
                  User
                </TableCell>
                <TableCell style={TableHeadStyle} sx={{ width: "7%" }} align="left">
                  Date
                </TableCell>
                <TableCell style={TableHeadStyle} sx={{ width: "5%" }} align="center">
                  Detail
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!tabledata || tabledata.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={12} align="center" sx={{ py: 5 }}>
                    <Typography variant="h6" color="textSecondary">
                      No transactions found
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                      Total in database: {finalData?.totalFilteredTransactions || 0}
                    </Typography>
                    <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                      Check browser console for filter details
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                tabledata.map((transaction, index) => (
                  <TableRow key={index}>
                    <TableCell component="th" scope="row" align="center" sx={{
                      ...ellipsisStyle,
                      textAlign: 'center'
                    }}>
                      {page * rowsPerPage + index + 1}
                    </TableCell>
                    <TableCell align="left" sx={ellipsisStyle} title={transaction?.transac_id}>
                      {transaction?.transac_id}
                    </TableCell>
                    <TableCell align="left" sx={ellipsisStyle} title={transaction?.unique_req_id || 'N/A'}>
                      {transaction?.unique_req_id || 'N/A'}
                    </TableCell>
                    <TableCell align="left" sx={ellipsisStyle} title={transaction?.transac_type}>
                      <Chip 
                        label={transaction?.transac_type} 
                        size="small"
                        color={transaction?.transac_type === 'StoreToBo' ? 'primary' : 'secondary'}
                      />
                    </TableCell>
                    <TableCell align="left" sx={ellipsisStyle} title={transaction?.consumable}>
                      {transaction?.consumable}
                    </TableCell>
                    <TableCell align="left" sx={ellipsisStyle} title={transaction?.partnumber}>
                      {transaction?.partnumber}
                    </TableCell>
                    <TableCell align="left" sx={ellipsisStyle} title={transaction?.asset_id || 'N/A'}>
                      {transaction?.asset_id || 'N/A'}
                    </TableCell>
                    <TableCell align="center" sx={{...ellipsisStyle, textAlign: 'center'}}>
                      <Chip 
                        label={transaction?.quantity} 
                        size="small"
                        color="info"
                      />
                    </TableCell>
                    <TableCell align="left" sx={ellipsisStyle}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        <Typography variant="caption" sx={{ fontSize: '10px', color: '#666' }}>
                          From: {transaction?.from_office}
                        </Typography>
                        <Typography variant="caption" sx={{ fontSize: '10px', color: '#666' }}>
                          To: {transaction?.to_office}
                        </Typography>
                      </div>
                    </TableCell>
                    <TableCell align="left" sx={ellipsisStyle}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        <Typography 
                          variant="caption" 
                          sx={{ fontSize: '10px', color: '#666' }}
                          title={transaction?.from_userid}
                        >
                          From: {transaction?.from_userid || 'N/A'}
                        </Typography>
                        <Typography 
                          variant="caption" 
                          sx={{ fontSize: '10px', color: '#666' }}
                          title={transaction?.to_userid}
                        >
                          To: {transaction?.to_userid || 'N/A'}
                        </Typography>
                      </div>
                    </TableCell>
                    <TableCell 
                      align="left" 
                      sx={ellipsisStyle}
                      title={moment(transaction?.time_stamp).format("DD-MM-YYYY HH:mm")}
                    >
                      {moment(transaction?.time_stamp).format("DD-MM-YYYY")}
                    </TableCell>
                    <TableCell align="center" sx={{
                      ...ellipsisStyle,
                      textAlign: 'center'
                    }}>
                      <DetailsIcon
                        sx={{ 
                          color: "green", 
                          fontSize: { xs: "20px", sm: "24px", md: "28px" }, 
                          "&:hover": { cursor: "pointer" } 
                        }}
                        onClick={(e) => handleDetailsClick(e, transaction)}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 100, 500, 1000]}
                  colSpan={12}
                  count={finalData?.totalFilteredTransactions || 0}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  SelectProps={{
                    inputProps: { "aria-label": "rows per page" },
                    native: true,
                  }}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                  sx={{
                    '.MuiTablePagination-toolbar': {
                      fontSize: { xs: '12px', sm: '14px' },
                      minHeight: { xs: '40px', sm: '52px' }
                    },
                    '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                      fontSize: { xs: '12px', sm: '14px' }
                    }
                  }}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </div>

      {/* Transaction Detail Modal */}
      {detailModalOpen && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1300,
            backdropFilter: 'blur(3px)'
          }}
          onClick={handleDetailModalClose}
        >
          <Paper 
            elevation={8}
            sx={{ 
              padding: 4, 
              maxWidth: 900, 
              width: '95%',
              maxHeight: '90vh',
              overflow: 'auto',
              borderRadius: 3,
              background: 'linear-gradient(to bottom, #ffffff 0%, #f8f9fa 100%)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <Box sx={{ 
              borderBottom: '2px solid #1976d2', 
              pb: 2, 
              mb: 3,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <Typography variant="h5" sx={{ fontWeight: 600, color: '#1976d2' }}>
                Consumable Transaction Details
              </Typography>
              <Chip 
                label={detailModalData.transac_type || 'N/A'} 
                color={detailModalData.transac_type === 'StoreToBo' ? 'primary' : 'secondary'}
                sx={{ fontWeight: 600 }}
              />
            </Box>

            {/* Basic Info Section */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#333', borderLeft: '4px solid #1976d2', pl: 2 }}>
                Basic Information
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, backgroundColor: 'white', p: 2, borderRadius: 2 }}>
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#666', textTransform: 'uppercase', fontSize: '11px' }}>
                    Transaction ID
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                    {detailModalData.transac_id || 'N/A'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#666', textTransform: 'uppercase', fontSize: '11px' }}>
                    Request ID
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                    {detailModalData.unique_req_id || 'N/A'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#666', textTransform: 'uppercase', fontSize: '11px' }}>
                    Transaction Date & Time
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                    {moment(detailModalData.time_stamp).format('DD/MM/YYYY HH:mm:ss')}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Consumable Details Section */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#333', borderLeft: '4px solid #ff9800', pl: 2 }}>
                Consumable Details
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, backgroundColor: 'white', p: 2, borderRadius: 2 }}>
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#666', textTransform: 'uppercase', fontSize: '11px' }}>
                    Consumable
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                    {detailModalData.consumable || 'N/A'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#666', textTransform: 'uppercase', fontSize: '11px' }}>
                    Part Number
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                    {detailModalData.partnumber || 'N/A'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#666', textTransform: 'uppercase', fontSize: '11px' }}>
                    Quantity
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                    <Chip 
                      label={detailModalData.quantity || 0} 
                      color="info"
                      sx={{ fontWeight: 600 }}
                    />
                  </Typography>
                </Box>
                {detailModalData.asset_id && (
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#666', textTransform: 'uppercase', fontSize: '11px' }}>
                      Linked Asset
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                      {(() => {
                        // asset_id now contains oem_tag, not MongoDB ObjectId
                        const asset = assets.find(a => a.oem_tag === detailModalData.asset_id);
                        return asset ? `${asset.name} (${detailModalData.asset_id})` : detailModalData.asset_id;
                      })()}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>

            {/* From/To Section */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#333', borderLeft: '4px solid #2e7d32', pl: 2 }}>
                Transfer Information
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
                {/* From Section */}
                <Box sx={{ backgroundColor: 'white', p: 2, borderRadius: 2, border: '1px solid #e0e0e0' }}>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: '#d32f2f', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#d32f2f' }} />
                    From
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#666', fontSize: '10px' }}>OFFICE</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{detailModalData.from_office || 'N/A'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#666', fontSize: '10px' }}>DESIGNATION</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{detailModalData.from_desig || 'N/A'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#666', fontSize: '10px' }}>STORE</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{detailModalData.from_store || 'N/A'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#666', fontSize: '10px' }}>USER EMAIL</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500, wordBreak: 'break-word' }}>
                        {detailModalData.from_userid || 'N/A'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#666', fontSize: '10px' }}>USER NAME</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {detailModalData.from_userid ? 
                          (() => {
                            const user = Object.values(users?.entities || {}).find(u => u.email === detailModalData.from_userid);
                            return user ? nameStringFn(user) : 'N/A';
                          })() : 
                          'N/A'
                        }
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* To Section */}
                <Box sx={{ backgroundColor: 'white', p: 2, borderRadius: 2, border: '1px solid #e0e0e0' }}>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: '#2e7d32', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#2e7d32' }} />
                    To
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#666', fontSize: '10px' }}>OFFICE</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{detailModalData.to_office || 'N/A'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#666', fontSize: '10px' }}>DESIGNATION</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{detailModalData.to_desig || 'N/A'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#666', fontSize: '10px' }}>STORE</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{detailModalData.to_store || 'N/A'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#666', fontSize: '10px' }}>USER EMAIL</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500, wordBreak: 'break-word' }}>
                        {detailModalData.to_userid || 'N/A'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#666', fontSize: '10px' }}>USER NAME</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {detailModalData.to_userid ? 
                          (() => {
                            const user = Object.values(users?.entities || {}).find(u => u.email === detailModalData.to_userid);
                            return user ? nameStringFn(user) : 'N/A';
                          })() : 
                          'N/A'
                        }
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* Footer Actions */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4, pt: 3, borderTop: '1px solid #e0e0e0' }}>
              <Button 
                variant="contained" 
                onClick={handleDetailModalClose}
                sx={{ 
                  px: 4,
                  textTransform: 'none',
                  fontWeight: 600
                }}
              >
                Close
              </Button>
            </Box>
          </Paper>
        </div>
      )}
    </>
  );
};

export default ConsumTransac;
